/**
 * Tools that look at the scene: frame the editor camera on something, and
 * capture what it sees as a picture the model reads. Both take the same
 * framing, so `screenshot({ object, angle })` lands on the pose `focus_camera`
 * would, and a look is one call.
 */

import '@babylonjs/core/Rendering/edgesRenderer';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { ScreenshotTools } from '@babylonjs/core/Misc/screenshotTools';
import { Pages } from '../../behaviors/pages';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import {
    alphaBetaToYawPitch, boundsAreFinite, describePose, fitOrbitRadius, orbitCameraPosition, orbitPoseFromBounds,
    type CapturePose, type Vec3, type WorldBounds,
} from '../camera/capturePose';
import { holdingDeck } from '../pages/holdingDeck';
import { defineTool, fail, resolveRef, type ToolInput } from './input';
import type { SceneTool, SceneToolHost, ToolImage, ToolOutcome } from './types';

/**
 * Named orbit angles as ArcRotateCamera [alpha, beta] radians: alpha is the
 * azimuth around +Y, beta the polar angle from +Y (0 top-down, PI/2 level). The
 * camera sits at (r cos a sin b, r cos b, r sin a sin b), so alpha = PI/2 puts it
 * at world +Z looking back: the scene's front, where the editor's own camera
 * starts, and, because TraceScene is right-handed, the only azimuth with world
 * +X on the screen's right. front and back were once swapped, which mirrored
 * every left/right judgement made from a capture.
 */
export const CAMERA_ANGLE_PRESETS: Readonly<Record<string, readonly [number, number]>> = {
    front: [Math.PI / 2, Math.PI / 2],
    back: [-Math.PI / 2, Math.PI / 2],
    left: [Math.PI, Math.PI / 2],
    right: [0, Math.PI / 2],
    top: [Math.PI / 2, 0.001],
    bottom: [Math.PI / 2, Math.PI - 0.001],
    iso: [Math.PI / 2 + Math.PI / 5, Math.PI / 3],
};

const FRAMING = {
    object: { type: 'string' },
    angle: { type: 'string', enum: Object.keys(CAMERA_ANGLE_PRESETS) },
    yaw: { type: 'number' },
    pitch: { type: 'number' },
    wide: { type: 'boolean' },
} as const;

const hasFraming = (input: ToolInput): boolean => Object.keys(FRAMING).some(key => input[key] !== undefined);

/** False for a mesh scaled to nothing, such as a hidden build step parked at its group's origin. */
function hasExtent(mesh: AbstractMesh): boolean {
    if (typeof mesh.getBoundingInfo !== 'function') return true;
    const { minimumWorld, maximumWorld } = mesh.getBoundingInfo().boundingBox;
    return !minimumWorld.equals(maximumWorld);
}

/**
 * World box of a node and what is under it. Drawn meshes first, so a switched-off
 * panel parked beside the subject does not pull the camera toward itself; every
 * mesh only when nothing is drawn.
 */
function hierarchyBounds(target: SceneObject, drawnOnly: boolean): WorldBounds | null {
    target.transform.computeWorldMatrix(true);
    const { min, max } = target.transform.getHierarchyBoundingVectors(true, drawnOnly ? mesh => mesh.isEnabled() && mesh.isVisible && hasExtent(mesh) : hasExtent);
    const bounds: WorldBounds = { min: [min.x, min.y, min.z], max: [max.x, max.y, max.z] };
    return boundsAreFinite(bounds) ? bounds : null;
}

/**
 * World box of the whole scene. When no mesh exists to measure (a headless scene
 * whose models never loaded) each object counts as the unit box its transform
 * scales, which is what a primitive is.
 */
function sceneBounds(scene: TraceScene): WorldBounds | null {
    const min: Vec3 = [Infinity, Infinity, Infinity];
    const max: Vec3 = [-Infinity, -Infinity, -Infinity];
    let found = false;
    const grow = (lo: Vec3, hi: Vec3): void => {
        found = true;
        for (let i = 0; i < 3; i++) {
            min[i] = Math.min(min[i], lo[i]);
            max[i] = Math.max(max[i], hi[i]);
        }
    };
    const objects = scene.getSceneObjects();
    for (const drawnOnly of [true, false]) {
        for (const object of objects) {
            const b = hierarchyBounds(object, drawnOnly);
            if (b) grow(b.min, b.max);
        }
        if (found) break;
    }
    if (!found) {
        for (const object of objects) {
            object.transform.computeWorldMatrix(true);
            const p = object.transform.absolutePosition;
            const s = object.transform.absoluteScaling;
            const h: Vec3 = [Math.abs(s.x) / 2, Math.abs(s.y) / 2, Math.abs(s.z) / 2];
            if (![p.x, p.y, p.z, ...h].every(Number.isFinite)) continue;
            grow([p.x - h[0], p.y - h[1], p.z - h[2]], [p.x + h[0], p.y + h[1], p.z + h[2]]);
        }
    }
    const bounds: WorldBounds = { min, max };
    return found && boundsAreFinite(bounds) ? bounds : null;
}

const round5 = (n: number): number => Number(n.toFixed(5));

/** An engine with no render loop never renders the frame a live capture waits for. */
function isHeadless(scene: TraceScene): boolean {
    const engine = scene.getBabylonScene().getEngine();
    return engine.constructor.name === 'NullEngine' || engine.activeRenderLoops.length === 0;
}

/**
 * Frame the editor camera (an orbit camera, when the scene has one) and work out
 * the pose arithmetically from the bounds, so a host with no render loop can
 * render from it. The live camera's settled target and radius win over the estimate.
 */
function frame(input: ToolInput, host: SceneToolHost, size: { width: number; height: number }): { error: string } | { summary: string; pose: CapturePose } {
    const babylon = host.scene.getBabylonScene();
    const cam = babylon.activeCamera instanceof ArcRotateCamera ? babylon.activeCamera : null;
    const wide = input.wide === true;

    // Read every input before the camera moves: a refused call leaves the user's view alone.
    const ref = input.object;
    if (ref !== undefined && typeof ref !== 'string') return { error: 'Error: object is a path or name.' };
    const target = ref && ref !== 'scene' ? resolveRef(host, ref) : null;
    if (ref && ref !== 'scene' && !target) return { error: `Error: Object "${ref}" not found.` };
    const angle = input.angle;
    const preset = typeof angle === 'string' ? CAMERA_ANGLE_PRESETS[angle.toLowerCase()] : undefined;
    if (angle !== undefined && !preset) return { error: `Error: Unknown angle "${String(angle)}"; use one of ${Object.keys(CAMERA_ANGLE_PRESETS).join(', ')}.` };
    for (const field of ['yaw', 'pitch'] as const) {
        if (input[field] !== undefined && (typeof input[field] !== 'number' || !Number.isFinite(input[field]))) return { error: `Error: ${field} is a number of degrees.` };
    }

    let framed: string;
    let bounds: WorldBounds | null;
    let fitted: WorldBounds | null = null;
    let framedPath: string | undefined;
    if (target) {
        bounds = hierarchyBounds(target, true) ?? hierarchyBounds(target, false);
        fitted = bounds;
        framedPath = target.getPath();
        cam?.setTarget(bounds
            ? new Vector3(...bounds.min).add(new Vector3(...bounds.max)).scale(0.5)
            : target.transform.absolutePosition.clone());
        if (!bounds) {
            const p = target.transform.absolutePosition;
            bounds = { min: [p.x - 0.5, p.y - 0.5, p.z - 0.5], max: [p.x + 0.5, p.y + 0.5, p.z + 0.5] };
        }
        framed = `framed "${target.name}"`;
    } else {
        if (cam) host.scene.frameScene();
        bounds = sceneBounds(host.scene);
        framed = 'framed the whole scene';
    }

    let angles = cam ? alphaBetaToYawPitch(cam.alpha, cam.beta) : undefined;
    let angleNote = '';
    if (preset) {
        if (cam) [cam.alpha, cam.beta] = preset;
        angles = alphaBetaToYawPitch(preset[0], preset[1]);
        angleNote = ` from the ${String(angle)} angle`;
    } else if (input.yaw !== undefined || input.pitch !== undefined) {
        const yaw = typeof input.yaw === 'number' ? input.yaw : undefined;
        const pitch = typeof input.pitch === 'number' ? input.pitch : undefined;
        if (cam) {
            if (yaw !== undefined) cam.alpha = yaw * Math.PI / 180;
            if (pitch !== undefined) cam.beta = pitch * Math.PI / 180;
        }
        angles = { yaw: yaw ?? angles?.yaw ?? NaN, pitch: pitch ?? angles?.pitch ?? NaN };
        angleNote = ` at yaw ${yaw ?? '-'}°, pitch ${pitch ?? '-'}°`;
    }

    // The object's box fills the frame from this angle with a tenth clear around it; wide doubles the distance.
    const margin = wide ? 2.2 : 1.1;
    if (cam && fitted) {
        cam.radius = fitOrbitRadius(fitted, cam.alpha, cam.beta, cam.fov, babylon.getEngine().getAspectRatio(cam), margin);
        cam.lowerRadiusLimit = cam.radius * 0.05;
        cam.upperRadiusLimit = cam.radius * 10;
    }

    const pose = orbitPoseFromBounds(bounds ?? { min: [-0.5, -0.5, -0.5], max: [0.5, 0.5, 0.5] }, {
        yaw: Number.isFinite(angles?.yaw) ? angles?.yaw : undefined,
        pitch: Number.isFinite(angles?.pitch) ? angles?.pitch : undefined,
        ...(fitted ? { fit: { fov: 0.8, aspect: size.width / size.height, margin } } : { radiusScale: wide ? 3 : 1.5 }),
        source: 'focus_camera',
        framed: framedPath,
    });
    // A live camera has settled its own target and radius against the real frustum; prefer them. A headless
    // engine's camera has no real canvas, so there the pose fitted at the capture's own size is what renders.
    if (cam && !isHeadless(host.scene) && Number.isFinite(cam.radius) && cam.radius > 0) {
        const t = cam.getTarget();
        pose.target = [round5(t.x), round5(t.y), round5(t.z)];
        pose.radius = round5(cam.radius);
        pose.position = orbitCameraPosition(pose.target, pose.yaw, pose.pitch, pose.radius);
    }
    host.capturePose?.(pose);
    return { summary: `Camera ${framed}${angleNote}${wide ? ' (wide)' : ''}.`, pose };
}

const focusCameraTool = defineTool({
    name: 'focus_camera',
    verb: 'Framing',
    edits: false,
    inputSchema: { type: 'object', properties: FRAMING },
    run(input, host) {
        const framed = frame(input, host, { width: 1280, height: 720 });
        if ('error' in framed) return Promise.resolve(fail(framed.error));
        return Promise.resolve({ text: `${framed.summary} Capture viewpoint: ${describePose(framed.pose)}.` });
    },
});

// ── Capture ──────────────────────────────────────────────────────────────────

const capturing = new WeakSet<TraceScene>();

/**
 * Capture without the editor's selection outline and gizmo, then select again
 * what was selected. A host's selection and the scene's can differ (the sandbox
 * keeps its own), so both are cleared and both put back.
 */
async function withoutSelection<T>(host: SceneToolHost, capture: () => Promise<T>): Promise<T> {
    const hostSelected = host.selected?.() ?? null;
    const sceneSelected = host.scene.getSelectedObject();
    if (!hostSelected && !sceneSelected) return capture();
    if (hostSelected && host.select) host.select(null);
    if (host.scene.getSelectedObject()) host.scene.select(null);
    try {
        return await capture();
    } finally {
        if (sceneSelected && !sceneSelected.transform.isDisposed() && !host.scene.getSelectedObject()) host.scene.select(sceneSelected);
        if (hostSelected && host.select && !hostSelected.transform.isDisposed()) host.select(hostSelected);
    }
}

/** The deck `object` is or sits in, else the scene's only deck. */
function deckFor(host: SceneToolHost, object: unknown): SceneObject {
    if (typeof object === 'string' && object && object !== 'scene') {
        for (let node = resolveRef(host, object); node; node = node.getParent()) {
            if (node.getBehavior(Pages)) return node;
        }
    }
    const decks = host.scene.getSceneObjects().filter(o => o.getBehavior(Pages));
    if (decks.length === 1) return decks[0];
    if (!decks.length) throw new Error('page needs a deck (an object with Pages); the scene has none.');
    throw new Error(`page: the scene has ${decks.length} decks (${decks.map(d => `"${d.name}"`).join(', ')}); pass the one to show as object.`);
}

/** Show page `page` (1 = the first) for this capture only, then leave the deck as it was. */
async function withPage<T>(host: SceneToolHost, page: unknown, object: unknown, capture: () => Promise<T>): Promise<T> {
    if (page === undefined) return capture();
    if (typeof page !== 'number' || !Number.isInteger(page) || page < 1) throw new Error('page must be a page number, 1 for the first.');
    const deck = deckFor(host, object);
    const count = deck.getBehavior(Pages)!.getPageCount();
    if (page > count) throw new Error(`page ${page}: "${deck.name}" has ${count} page${count === 1 ? '' : 's'}.`);
    return holdingDeck(deck, show => { show(page - 1); return capture(); });
}

/** Hide every mesh outside `only`'s subtrees for this capture: a render mask, never an edit. */
async function withOnly<T>(host: SceneToolHost, only: unknown, capture: () => Promise<T>): Promise<T> {
    if (only === undefined) return capture();
    if (!Array.isArray(only) || !only.length || only.length > 128 || only.some(ref => typeof ref !== 'string' || !ref.trim())) {
        throw new Error('only must name 1-128 objects; what is under them is included.');
    }
    const roots = new Set<SceneObject>(only.map(ref => {
        const object = resolveRef(host, ref);
        if (!object) throw new Error(`Object "${String(ref)}" not found.`);
        return object;
    }));
    const included = new Set<AbstractMesh>();
    for (const object of host.scene.getSceneObjects()) {
        for (let node: SceneObject | null = object; node; node = node.getParent()) {
            if (!roots.has(node)) continue;
            for (const mesh of object.getOwnedMeshes()) included.add(mesh);
            break;
        }
    }
    if (![...included].some(mesh => mesh.isEnabled() && mesh.isVisible && mesh.visibility > 0 && mesh.getTotalVertices() > 0)) {
        throw new Error('Nothing under only is visible and loaded.');
    }
    const hidden = host.scene.getBabylonScene().meshes.filter(mesh => mesh.isVisible && !included.has(mesh));
    try {
        for (const mesh of hidden) mesh.isVisible = false;
        return await capture();
    } finally {
        for (const mesh of hidden) if (!mesh.isDisposed()) mesh.isVisible = true;
    }
}

/** Temporary contrasting edge lines, to show seams between same-colour parts without editing materials. */
async function withEdges<T>(host: SceneToolHost, edges: unknown, capture: () => Promise<T>): Promise<T> {
    if (edges === undefined || edges === false) return capture();
    if (edges !== true) throw new Error('edges is true or false.');
    const meshes = host.scene.getBabylonScene().meshes.filter(mesh => mesh.isEnabled() && mesh.isVisible
        && mesh.visibility > 0 && mesh.getTotalIndices() > 0 && !mesh.getClassName().includes('LinesMesh'));
    const triangles = meshes.reduce((n, mesh) => n + mesh.getTotalIndices() / 3, 0);
    if (meshes.length > 2000 || triangles > 100000) throw new Error('An edge overlay is limited to 2,000 meshes and 100,000 triangles; narrow it with only.');
    if (!meshes.length) throw new Error('No visible geometry to outline.');
    const states = meshes.map(mesh => ({ mesh, renderer: mesh.edgesRenderer, enabled: mesh.edgesRenderer?.isEnabled, width: mesh.edgesWidth, color: mesh.edgesColor }));
    try {
        for (const { mesh, renderer } of states) {
            if (!renderer) mesh.enableEdgesRendering(0.95, false, { useAlternateEdgeFinder: true, useFastVertexMerger: true });
            mesh.edgesRenderer!.isEnabled = true;
            mesh.edgesWidth = 1;
            const material = mesh.material as { albedoColor?: { r: number; g: number; b: number }; diffuseColor?: { r: number; g: number; b: number } } | null;
            const color = material?.albedoColor ?? material?.diffuseColor;
            const line = color && color.r + color.g + color.b > 1.2 ? 0 : 1;
            mesh.edgesColor = new Color4(line, line, line, 1);
        }
        return await capture();
    } finally {
        for (const { mesh, renderer, enabled, width, color } of states) {
            if (mesh.isDisposed()) continue;
            if (!renderer) mesh.disableEdgesRendering();
            else renderer.isEnabled = enabled!;
            mesh.edgesWidth = width;
            mesh.edgesColor = color;
        }
    }
}

const CAPTURE_TIMEOUT_MS = 15_000;

function asImage(dataUrl: string): ToolImage | null {
    const match = /^data:(image\/(?:png|jpeg));base64,(.+)$/s.exec(dataUrl);
    return match ? { mimeType: match[1] as ToolImage['mimeType'], data: match[2] } : null;
}

const screenshotTool = defineTool({
    name: 'screenshot',
    verb: 'Looking at',
    edits: false,
    inputSchema: {
        type: 'object',
        properties: {
            ...FRAMING,
            edges: { type: 'boolean' },
            only: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 128 },
            width: { type: 'number' },
            height: { type: 'number' },
            format: { type: 'string', enum: ['png', 'jpeg'] },
            page: { type: 'integer', minimum: 1 },
            final_review: { type: 'boolean' },
        },
    },
    async run(input, host) {
        const scene = host.scene;
        const engine = scene.getBabylonScene().getEngine();
        const headless = isHeadless(scene);
        if (headless && (input.only !== undefined || input.edges === true)) {
            return fail('Error: only and edges need a live renderer; this one is headless.');
        }
        if (capturing.has(scene)) return fail('Error: A screenshot of this scene is already being taken.');
        const width = input.width === undefined ? 1280 : typeof input.width === 'number' ? Math.round(input.width) : NaN;
        const height = input.height === undefined ? 720 : typeof input.height === 'number' ? Math.round(input.height) : NaN;
        if (!(width >= 64 && width <= 4096 && height >= 64 && height <= 4096)) return fail('Error: width and height are 64-4096 px.');
        if (input.format !== undefined && input.format !== 'png' && input.format !== 'jpeg') return fail('Error: format is png or jpeg.');
        const mimeType = input.format === 'jpeg' ? 'image/jpeg' : 'image/png';
        // Refuse before framing, so a call that can't capture leaves the view alone.
        if (headless && !host.capture) return fail('Error: Screenshots are unavailable here (no renderer). Check the scene with get_scene_tree or get_transform instead.');
        // "selected" names the selection, which the capture clears to keep its outline out of the picture: pin it to a path first.
        const pinned = (ref: unknown): unknown => ref === 'selected' ? resolveRef(host, ref)?.getPath() ?? ref : ref;
        const call: ToolInput = { ...input, object: pinned(input.object), ...(Array.isArray(input.only) ? { only: input.only.map(pinned) } : {}) };
        capturing.add(scene);
        try {
            return await withoutSelection(host, () => withPage(host, call.page, call.object, () => withOnly(host, call.only, () => withEdges(host, call.edges, async (): Promise<ToolOutcome> => {
                let summary = 'Captured the current view.';
                let pose: CapturePose | null = null;
                if (hasFraming(call)) {
                    const framed = frame(call, host, { width, height });
                    if ('error' in framed) return fail(framed.error);
                    summary = framed.summary;
                    pose = framed.pose;
                }
                if (headless && host.capture) {
                    const answer = await host.capture({ pose, width, height, mimeType, finalReview: call.final_review === true });
                    return typeof answer === 'string' ? fail(`Error: ${answer}`) : { text: summary, images: [answer] };
                }
                const camera = scene.getBabylonScene().activeCamera;
                if (!camera) return fail('Error: The scene has no active camera.');
                let timer: ReturnType<typeof setTimeout> | undefined;
                const dataUrl = await Promise.race([
                    ScreenshotTools.CreateScreenshotAsync(engine, camera, { width, height }, mimeType, 0.85),
                    new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS); }),
                ]).finally(() => clearTimeout(timer));
                if (dataUrl === null) return fail('Error: No frame was rendered within 15 s.');
                const image = asImage(dataUrl);
                return image ? { text: summary, images: [image] } : fail('Error: The capture returned no image.');
            }))));
        } catch (error) {
            return fail(`Error: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            capturing.delete(scene);
        }
    },
});

export const VIEW_TOOLS: readonly SceneTool[] = [focusCameraTool, screenshotTool];
